import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonFileHandling = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Advanced File Handling</h1>
                <p className="subtitle">Reading, writing, and streaming data safely. Mastering pointers, buffers, and encodings.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Imagine a file is a <strong>Book</strong> in a Library.
                    </p>
                    <ul>
                        <li><strong>Opening (`open()`):</strong> You take the book off the shelf. You explicitly tell the librarian if you ostensibly just want to <strong>Read</strong> it (`'r'`) or if you plan to <strong>Write</strong> notes in it (`'w'`).</li>
                        <li><strong>The Cursor (Pointer):</strong> When you read, you use your finger to track words. When you stop, your finger stays there. If you want to read the beginning again, you must move your finger back (`seek(0)`).</li>
                        <li><strong>Closing (`close()`):</strong> You MUST put the book back on the shelf so others can use it. If you keep it in your bag (forget to close), the Library assumes it's missing (File Lock).</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>2. The Modes of Operation</h2>
                <p>
                    `open()` is powerful because of its modes. Choosing the wrong one can wipe your data instantly.
                </p>
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Mode</th>
                            <th>Name</th>
                            <th>Behavior</th>
                            <th>Pointer Position</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>`'r'`</td>
                            <td>Read</td>
                            <td>Default. Errors if file missing.</td>
                            <td>Start (0)</td>
                        </tr>
                        <tr>
                            <td>`'w'`</td>
                            <td>Write</td>
                            <td><strong>TRUNCATES (Deletes)</strong> existing content! Creates new file if missing.</td>
                            <td>Start (0)</td>
                        </tr>
                        <tr>
                            <td>`'a'`</td>
                            <td>Append</td>
                            <td>Safe writing. Adds to the end. Creates new file if missing.</td>
                            <td>End (EOF)</td>
                        </tr>
                        <tr>
                            <td>`'x'`</td>
                            <td>Exclusive</td>
                            <td><strong>Fails</strong> if file exists. Good for "Create only".</td>
                            <td>Start (0)</td>
                        </tr>
                        <tr>
                            <td>`'r+'`</td>
                            <td>Update</td>
                            <td>Read AND Write. No truncation.</td>
                            <td>Start (0)</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="content-section">
                <h2>3. Text vs Binary & Encodings</h2>
                <p>
                    Files are just bytes on a disk. Python tries to decode them into Strings for you.
                    This process is called <strong>Decoding</strong> and requires an encoding standard (like UTF-8).
                </p>
                <CodeBlock language="python" code={`# 1. Text Mode (Default)
# Python decodes bytes -&gt; str automatically
with open("message.txt", "r", encoding="utf-8") as f:
    content = f.read()
    print(type(content)) # <class 'str'>

# 2. Binary Mode ('b')
# Raw bytes. Essential for Images, PDFs, EXEs.
with open("logo.png", "rb") as f:
    data = f.read()
    print(type(data)) # <class 'bytes'>
    print(data[:10])  # b'\\x89PNG\\r\\n\\x1a...'`} />
                <ProTip><p><strong>Cross-Platform Hazard:</strong> On Windows, the default encoding is often `cp1252`. On Linux, it's `utf-8`. If you don't explicitly say `encoding="utf-8"`, your code will crash when you move it to a server. Always be explicit!</p></ProTip>
            </div>

            <div className="content-section">
                <h2>4. Deep Dive: Streaming Large Files</h2>
                <p>
                    What if `server.log` is 100GB?
                    If you do `f.read()`, Python tries to load 100GB into RAM. Your computer crashes.
                    Solution: <strong>Stream it</strong> line-by-line (or chunk-by-chunk).
                </p>
                <CodeBlock language="python" code={`# BAD 
with open("huge_log.txt") as f:
    data = f.read() # RAM Spike! 

# GOOD : The file object is an Iterator!
with open("huge_log.txt") as f:
    for line in f:
        # Memory usage: Only length of 1 line (e.g., 1KB)
        if "ERROR" in line:
            print(line)`} />
                <p>
                    For binary files (no lines), read in chunks:
                </p>
                <CodeBlock language="python" code={`# GOOD (Binary Chunking)
CHUNK_SIZE = 4096 # 4KB
with open("video.mp4", "rb") as f:
    while True:
        chunk = f.read(CHUNK_SIZE)
        if not chunk:
            break
        process(chunk)`} />
            </div>

            <div className="content-section">
                <h2>5. Controlling the Cursor: `seek()` and `tell()`</h2>
                <p>
                    You can manually move the read/write head. This is useful for random access (e.g., reading a database header).
                </p>
                <CodeBlock language="python" code={`with open("secret.txt", "w+") as f:
    f.write("Hello World")
    
    print(f.tell()) # 11 (Pointer is at end)
    
    # f.read() here would return "" because we are at the end!
    
    f.seek(0) # Rewind to start
    print(f.read()) # "Hello World"`} />
            </div>

            <div className="content-section">
                <h2>6. Buffering Strategy</h2>
                <p>
                    Writing to a hard drive is slow. Python optimizes this by keeping a <strong>Buffer</strong> (Temporary RAM storage).
                    It only physically writes to disk when:
                </p>
                <ol>
                    <li>The buffer is full (usually 4KB or 8KB).</li>
                    <li>You call `f.flush()`.</li>
                    <li>You call `f.close()` (or exit the `with` block).</li>
                </ol>
                <p>
                    This is why if your program crashes before closing, your file might be empty! The data was still in the buffer.
                    Use `with` blocks to ensure buffers are always flushed.
                </p>
            </div>

        </div>
    );
};

export default PythonFileHandling;


